import * as React from 'react'
import { useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { ColumnOption, ITEM_TYPE_OPTION, SortableDragItem } from './types'
import I18n from '../../../libs/i18n'

export interface OptionRowProps {
  option: ColumnOption
  index: number
  moveOption: (dragIndex: number, hoverIndex: number) => void
  updateOption: (id: number | string, updates: Partial<ColumnOption>) => void
  removeOption: (id: number | string) => void
}

export default function OptionRow({
  option,
  index,
  moveOption,
  updateOption,
  removeOption,
}: OptionRowProps) {
  const ref = useRef<HTMLDivElement>(null)

  const [, drop] = useDrop<SortableDragItem, void, unknown>({
    accept: ITEM_TYPE_OPTION,
    hover(item, monitor) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return

      const hoverBoundingRect = ref.current.getBoundingClientRect()
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      if (!clientOffset) return
      const hoverClientY = clientOffset.y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return

      moveOption(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag<SortableDragItem, void, { isDragging: boolean }>({
    item: { type: ITEM_TYPE_OPTION, id: option.id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  drag(drop(ref))

  return (
    <div
      ref={ref}
      className="d-flex align-items-center mb-2"
      style={{ opacity: isDragging ? 0.5 : 1 }}
    >
      <i className="fa fa-grip-vertical text-muted mr-2" style={{ cursor: 'grab' }} />
      <input
        type="text"
        className="form-control form-control-sm mr-2"
        style={{ flex: 2 }}
        placeholder={I18n.t('project_groups.columns_editor.option_label_placeholder')}
        value={option.label}
        onChange={(e) => updateOption(option.id, { label: e.target.value })}
      />
      <input
        type="text"
        className="form-control form-control-sm mr-2"
        style={{ flex: 1 }}
        placeholder={I18n.t('project_groups.columns_editor.option_value_placeholder')}
        value={option.value || ''}
        onChange={(e) => updateOption(option.id, { value: e.target.value })}
      />
      <input
        type="color"
        className="form-control form-control-sm mr-2"
        style={{ width: 50, padding: 2 }}
        value={option.color || '#ffffff'}
        onChange={(e) => updateOption(option.id, { color: e.target.value })}
        title={I18n.t('project_groups.columns_editor.option_color')}
      />
      <button
        type="button"
        className="btn btn-outline-danger btn-sm"
        onClick={() => removeOption(option.id)}
        title={I18n.t('project_groups.columns_editor.remove_option')}
      >
        <i className="fa fa-trash" />
      </button>
    </div>
  )
}
